import { useCallback, useRef } from 'react';

import { computeChangedFields } from './changedFields';

/**
 * Adds change-tracking to an already-built form: a `setValues` wrapper that moves the tracked
 * baseline forward on every call (from anywhere, not just this hook's own caller), and
 * `getChangedValues()` - the top-level fields that differ from that baseline, whole if a compound
 * value changed anywhere inside it (see `computeChangedFields`).
 *
 * Factored out of `useFormPolicy` so a test harness building its own form outside `useFormPolicy`
 * (e.g. to support an explicit `initialValues` override) can still wrap it identically, rather than
 * hand-rolling a second copy of this logic that could drift from the real one.
 *
 * `F`'s own `values`/`setValues` are read through `any` internally on purpose: a mapped type like
 * `FormValuesOf<TFields>` cannot be proven to extend `object` while its own `TFields` is still a
 * type parameter at the call site (same friction `computeChangedFields` documents), so this widens
 * rather than forcing every caller to fight that constraint. The exposed return type stays
 * precise - it comes from `F` itself, inferred concretely at each real call site.
 */
export const useChangedFieldsTracking = <F extends { values: any; setValues: (values: any) => void }>(
  form: F,
  initialValues: F['values'],
  trackChangedFields = true,
): F & { getChangedValues: (values?: F['values']) => Partial<F['values']> } => {
  // Only the FIRST render's argument is used, so later re-renders recomputing `initialValues`
  // don't reset the baseline.
  const baselineRef = useRef(initialValues);

  const setValues = useCallback<F['setValues']>(
    ((values: any) => {
      const resolved = typeof values === 'function' ? values(form.values) : values;
      baselineRef.current = { ...(baselineRef.current as Record<string, unknown>), ...(resolved as Record<string, unknown>) };
      form.setValues(resolved);
    }) as F['setValues'],
    [form],
  );

  const getChangedValues = useCallback(
    (values: F['values'] = form.values): Partial<F['values']> =>
      (trackChangedFields ? computeChangedFields(baselineRef.current as Record<string, unknown>, values as Record<string, unknown>) : values) as Partial<
        F['values']
      >,
    [trackChangedFields, form.values],
  );

  return { ...form, setValues, getChangedValues };
};
